import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useClipboard } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconCopy, IconEdit, IconRestore, IconTrash } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import {
  useCompanyOptions,
  useDeleteDocuments,
  useModule,
  useNames,
  useUpdateDocument,
} from "../../api/hooks";
import type { DocumentCard } from "../../api/types";
import { useAuth } from "../../auth/auth";
import { formatBytes, formatDate, formatDateTime } from "../../lib/format";

export function MetadataPanel({ doc }: { doc: DocumentCard }) {
  const { t } = useTranslation();
  const { company, docType } = useNames();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const remove = useDeleteDocuments("delete");
  const restore = useDeleteDocuments("restore");
  const deleteOn = useModule("delete");
  const isAdmin = user?.role === "admin" && deleteOn;
  const clipboard = useClipboard({ timeout: 1500 });
  const rows: [string, React.ReactNode][] = [
    [t("document.regNumber"), doc.regNumber ?? "—"],
    [t("document.date"), doc.docDate ? formatDate(doc.docDate) : "—"],
    [t("document.company"), company(doc.companyId) || "—"],
    [t("document.type"), doc.docTypeId ? docType(doc.docTypeId) : "—"],
    [t("document.pagesLabel"), String(doc.pageCount)],
    [t("document.file"), `${doc.originalName} (${formatBytes(doc.sizeBytes)})`],
    [t("document.version"), String(doc.version)],
    [t("document.indexed"), doc.indexedAt ? formatDateTime(doc.indexedAt) : "—"],
  ];
  const location = doc.locations.find((l) => l.present) ?? doc.locations[0];
  // Other places the same file still lies in (an old path of a moved file does not count).
  const otherPlaces = doc.locations.filter((l) => l.present && l !== location).length;
  if (location)
    rows.push([
      t("sources.source"),
      <Stack key="loc" gap={2}>
        <Group gap={4} wrap="nowrap" align="flex-start">
          <Text
            size="sm"
            style={{ wordBreak: "break-all" }}
            c={location.present ? "inherit" : "dimmed"}
          >
            {location.path}
          </Text>
          <Tooltip label={clipboard.copied ? t("sources.pathCopied") : t("sources.copyPath")}>
            <ActionIcon
              size="sm"
              variant="subtle"
              aria-label={t("sources.copyPath")}
              onClick={() => clipboard.copy(location.path)}
            >
              <IconCopy size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>
        <Text size="xs" c="dimmed">
          {location.sourceName}
          {otherPlaces > 0 ? ` · ${t("sources.moreLocations", { count: otherPlaces })}` : ""}
          {location.present ? "" : ` · ${t("sources.locationGone")}`}
        </Text>
      </Stack>,
    ]);
  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Text fw={600}>{t("document.metadata")}</Text>
        <Button
          size="compact-xs"
          variant="subtle"
          leftSection={<IconEdit size={14} />}
          onClick={() => setEditing(true)}
        >
          {t("common.edit")}
        </Button>
      </Group>
      <Table verticalSpacing={2} withRowBorders={false}>
        <Table.Tbody>
          {rows.map(([k, v]) => (
            <Table.Tr key={k}>
              <Table.Td c="dimmed" w={110} style={{ verticalAlign: "top" }}>
                {k}
              </Table.Td>
              <Table.Td style={{ wordBreak: "break-word" }}>{v}</Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      <Group gap={6}>
        {doc.pages.ocr > 0 && (
          <Badge color="orange" variant="light">
            OCR: {doc.pages.ocr}
          </Badge>
        )}
        {doc.pages.legacy > 0 && (
          <Badge color="yellow" variant="light">
            {t("document.legacy")}: {doc.pages.legacy}
          </Badge>
        )}
        {doc.status !== "ready" && (
          <Badge color="gray" variant="light">
            {doc.status}
          </Badge>
        )}
        {doc.metadataConfidence !== null && doc.metadataConfidence < 0.8 && (
          <Badge color="red" variant="light">
            {t("document.metadataUnsure")}
          </Badge>
        )}
      </Group>
      {doc.versions.length > 1 && (
        <Stack gap={2}>
          <Text size="sm" fw={500}>
            {t("document.versions")}
          </Text>
          {doc.versions.map((v) => (
            <Text key={v.id} size="sm">
              {v.id === doc.id ? (
                <b>
                  v{v.version} ({t(`document.status.${v.status}`, v.status)})
                </b>
              ) : (
                <Anchor component={Link} to={`/documents/${v.id}`}>
                  v{v.version} ({t(`document.status.${v.status}`, v.status)})
                </Anchor>
              )}{" "}
              <Text span c="dimmed">
                {formatDate(v.created_at)}
              </Text>
            </Text>
          ))}
        </Stack>
      )}
      {isAdmin && doc.status !== "deleted" && (
        <Button
          size="compact-xs"
          variant="subtle"
          color="red"
          leftSection={<IconTrash size={14} />}
          onClick={() => setConfirmDelete(true)}
          style={{ alignSelf: "flex-start" }}
        >
          {t("common.delete")}
        </Button>
      )}
      {isAdmin && doc.status === "deleted" && (
        <Button
          size="compact-xs"
          variant="light"
          leftSection={<IconRestore size={14} />}
          loading={restore.isPending}
          style={{ alignSelf: "flex-start" }}
          onClick={() =>
            restore
              .mutateAsync([doc.id])
              .then(() => notifications.show({ message: t("document.restored"), color: "green" }))
              .catch((e: Error) => notifications.show({ message: e.message, color: "red" }))
          }
        >
          {t("document.restore")}
        </Button>
      )}
      <Modal
        opened={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title={t("common.delete")}
      >
        <Stack>
          <Text size="sm">{t("document.deleteConfirm", { title: doc.title })}</Text>
          <Text size="xs" c="dimmed">
            {t("document.deleteHint")}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmDelete(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              loading={remove.isPending}
              onClick={() =>
                remove
                  .mutateAsync([doc.id])
                  .then(() => {
                    notifications.show({ message: t("document.deleted"), color: "green" });
                    navigate("/documents");
                  })
                  .catch((e: Error) => notifications.show({ message: e.message, color: "red" }))
              }
            >
              {t("common.delete")}
            </Button>
          </Group>
        </Stack>
      </Modal>
      {/* Keyed by document: the form seeds its state once, and a cached card switch keeps this
          panel mounted, so without the key it would PATCH the new document with the old values. */}
      <EditModal key={doc.id} doc={doc} opened={editing} onClose={() => setEditing(false)} />
    </Stack>
  );
}

function EditModal({
  doc,
  opened,
  onClose,
}: {
  doc: DocumentCard;
  opened: boolean;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const { lookups, docType } = useNames();
  const update = useUpdateDocument(doc.id);
  const [title, setTitle] = useState(doc.title);
  const [regNumber, setRegNumber] = useState(doc.regNumber ?? "");
  const [docDate, setDocDate] = useState<string | null>(doc.docDate);
  const [companyId, setCompanyId] = useState(String(doc.companyId));
  const [docTypeId, setDocTypeId] = useState<string | null>(
    doc.docTypeId ? String(doc.docTypeId) : null,
  );
  const companyOptions = useCompanyOptions(companyId);

  const save = async () => {
    try {
      await update.mutateAsync({
        title: title.trim() || doc.title,
        regNumber: regNumber.trim() || null,
        docDate,
        companyId: Number(companyId),
        docTypeId: docTypeId ? Number(docTypeId) : null,
      });
      notifications.show({ message: t("common.saved"), color: "green" });
      onClose();
    } catch (e) {
      notifications.show({ message: (e as Error).message, color: "red" });
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t("document.editTitle")}>
      <Stack>
        <TextInput
          label={t("document.title")}
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
        />
        <TextInput
          label={t("document.regNumber")}
          value={regNumber}
          onChange={(e) => setRegNumber(e.currentTarget.value)}
        />
        <DateInput
          label={t("document.date")}
          value={docDate}
          onChange={setDocDate}
          valueFormat="DD.MM.YYYY"
          clearable
        />
        <Select
          label={t("document.company")}
          data={companyOptions}
          value={companyId}
          onChange={(v) => v && setCompanyId(v)}
          allowDeselect={false}
        />
        <Select
          label={t("document.type")}
          data={(lookups.data?.docTypes ?? []).map((d) => ({
            value: String(d.id),
            label: docType(d.id),
          }))}
          value={docTypeId}
          onChange={setDocTypeId}
          clearable
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button onClick={save} loading={update.isPending}>
            {t("common.save")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
